import {
  assetObservationsResponse,
  assetOwnerInvitationCreateResponseSchema,
  assetsResolveResponseSchema,
  assetStateResponse,
  assignableAssetsResponseSchema,
  type AssetObservationsResponse,
  type AssetOwnerInvitationCreateRequest,
  type AssetOwnerInvitationCreateResponse,
  type AssetsResolveRequest,
  type AssetsResolveResponse,
  type AssetStateResponse,
  type AssignableAssetsResponse,
  type VisitContactResponse,
  visitContactResponse,
  unitDeliveryResponse,
  type UnitDeliveryBody,
  type UnitDeliveryResponse,
} from "@bitacorapro/api-types";
import { request } from "../client";

// POST /assets/resolve — bulk id → name lookup for the Equipo screen's
// scope-composition. Ingress parse per ADR-0021/D6 to catch wire-skew
// between a cached dashboard bundle and a newer core-api deploy.
export const assetsApi = {
  // GET /assets/:id/visit-contact (ADR-0150) — el contacto para visitas
  // vigente de la unidad (o null), para coordinar el acceso al asignar.
  getVisitContact: async (assetId: string): Promise<VisitContactResponse> => {
    const json = await request<unknown>("GET", "core", `/assets/${assetId}/visit-contact`);
    return visitContactResponse.parse(json);
  },
  resolve: async (body: AssetsResolveRequest): Promise<AssetsResolveResponse> => {
    const json = await request<unknown>("POST", "core", "/assets/resolve", { body });
    return assetsResolveResponseSchema.parse(json);
  },
  // GET /assets/assignable — flat, unpaginated store list for the
  // self-service invite forms' store picker (ADR-0116).
  listAssignable: async (): Promise<AssignableAssetsResponse> => {
    const json = await request<unknown>("GET", "core", "/assets/assignable");
    return assignableAssetsResponseSchema.parse(json);
  },
  // POST /assets/:id/owner-invitation (ADR-0124) — "activar la unidad ES
  // invitar a su propietario". The body carries only who; the role and
  // the unit are resolved server-side from the recipe primitive and the
  // `:id`, so there is nothing else to send.
  //
  // `idempotencyKey` is a required parameter and is NOT minted here. The
  // route creates a permanent artifact (an invited account + an activated
  // unit) behind a synchronous call, so a lost response plus a retry with
  // a fresh key is two invitations to the same address (#0472). The
  // caller owns the key for the lifetime of one form session and replays
  // the same value on retry — the plugin caches only 2xx, so a retry
  // after a fan-out 400 re-executes under the same key, which is exactly
  // the desired behaviour. Minting per call here would defeat all of it.
  //
  // Ingress `.parse` per ADR-0021/D6, same posture as the two reads above.
  inviteOwner: async (
    assetId: string,
    body: AssetOwnerInvitationCreateRequest,
    idempotencyKey: string
  ): Promise<AssetOwnerInvitationCreateResponse> => {
    const json = await request<unknown>("POST", "core", `/assets/${assetId}/owner-invitation`, {
      body,
      headers: { "Idempotency-Key": idempotencyKey },
    });
    return assetOwnerInvitationCreateResponseSchema.parse(json);
  },
  // PUT /assets/:id/entrega (F9, fase 2 corte 8) — fija o cambia el día y
  // la hora de inicio de la entrega. Misma postura de clave que
  // inviteOwner: la pone el llamador, una por sesión del formulario.
  setDelivery: async (
    assetId: string,
    body: UnitDeliveryBody,
    idempotencyKey: string
  ): Promise<UnitDeliveryResponse> => {
    const json = await request<unknown>("PUT", "core", `/assets/${assetId}/entrega`, {
      body,
      headers: { "Idempotency-Key": idempotencyKey },
    });
    return unitDeliveryResponse.parse(json);
  },
  // GET /assets/:id/observations — LIVE observation rows for one store
  // (ADR-0058/0065). The closed-report view filters these by reportId
  // for its "Observaciones levantadas" table; it is the only non-frozen
  // data on that screen («Estado hoy»).
  getObservations: async (assetId: string): Promise<AssetObservationsResponse> => {
    const json = await request<unknown>("GET", "core", `/assets/${assetId}/observations`);
    return assetObservationsResponse.parse(json);
  },
  // GET /assets/:id/state (ADR-0137) — per checklist template, the latest
  // closed acta's sections with conformance; feeds DetalleTienda's
  // «Estado por sección» strip. Same ingress parse.
  getState: async (assetId: string): Promise<AssetStateResponse> => {
    const json = await request<unknown>("GET", "core", `/assets/${assetId}/state`);
    return assetStateResponse.parse(json);
  },
};
